/* eslint-disable quotes */
import type { TypeLang, TypeCmsMediumImage, TypeJsonLdProductData } from '@/schema'
import { buildUrl, buildCanonicalUrl, buildBreadcrumb, getIetfLang, BREADCRUMB_HOME, BREADCRUMB_CATALOG } from './_url'
import { buildOrganization } from './_organization'

type TypePropertyLabels = { presentations: string, sizes: string, colors: string }

const PROPERTY_LABELS: Record<TypeLang, TypePropertyLabels> = {
  es: { presentations: 'Presentaciones', sizes: 'Tamaños disponibles', colors: 'Colores disponibles' },
  en: { presentations: 'Presentations', sizes: 'Available sizes', colors: 'Available colors' }
}

/** Same order `_parser.ts` uses to resolve the best CMS image variant. */
const VARIANT_PRIORITY = ['l', 'm', 's', 'sliderL', 'sliderM', 'sliderS'] as const

/**
 * The CMS marks `section` / `group` / `family` as optional and their type
 * definitions carry no `name`, so read it structurally rather than trusting the type.
 */
function getName(node: unknown): string {
  if (!node || typeof node !== 'object') return ''
  const { name } = node as { name?: unknown }
  return typeof name === 'string' ? name.trim() : ''
}

function getImageSrc(medium?: TypeCmsMediumImage): string {
  const image = medium?.image as Record<string, unknown> | undefined
  if (!image) return ''

  for (const key of VARIANT_PRIORITY) {
    const variant = image[key]
    if (!variant || typeof variant !== 'object') continue

    const { src } = variant as { src?: unknown }
    if (typeof src === 'string' && src.length > 0) return src
  }

  return ''
}

function getNames(options?: Array<{ name?: string }>): string[] {
  if (!Array.isArray(options)) return []
  return options.map(option => option?.name).filter((name): name is string => typeof name === 'string' && name.length > 0)
}

export function jsonLdProduct(data: TypeJsonLdProductData, locale: TypeLang) {
  const { product, erpid } = data
  const { name, material, seoDescription, familySlug, media, medium, presentations, sizes, colors } = product ?? {}

  const url = buildCanonicalUrl(locale, `/model/${erpid}`)
  const sku = product?.erpId || product?.info?.erpId || erpid

  const sectionName = getName(product?.section)
  const groupName = getName(product?.group)
  const familyName = getName(product?.family)
  const categoryPath = [sectionName, groupName, familyName].filter(Boolean)

  // The CMS has no product description, so compose one the same way the page metadata does.
  const composedDescription = [material, categoryPath.join(' / ')].filter(Boolean).join(' · ')
  const description = seoDescription?.trim() || composedDescription

  const images = [...(Array.isArray(media) ? media : []), ...(medium ? [medium] : [])].map(getImageSrc).filter(Boolean)
  const uniqueImages = [...new Set(images)]

  const labels = PROPERTY_LABELS[locale]
  const presentationNames = getNames(presentations)

  const additionalProperty = [
    ...presentationNames.length > 0 ? [{ "@type": "PropertyValue", "name": labels.presentations, "value": presentationNames.join(', ') }] : [],
    ...sizes?.length > 0 ? [{ "@type": "PropertyValue", "name": labels.sizes, "value": sizes.length }] : [],
    ...colors?.length > 0 ? [{ "@type": "PropertyValue", "name": labels.colors, "value": colors.length }] : []
  ]

  const breadcrumbItems = [
    { name: BREADCRUMB_HOME[locale], path: '' },
    { name: BREADCRUMB_CATALOG[locale], path: '/catalog' },
    ...typeof familySlug === 'string' && familySlug && familyName ? [{ name: familyName, path: `/catalog/${familySlug}` }] : [],
    { name: name || sku, path: `/model/${erpid}` }
  ]

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemPage",
        "@id": `${url}#webpage`,
        "url": url,
        "name": name || sku,
        ...description && { "description": description },
        "inLanguage": getIetfLang(locale),
        "isPartOf": {
          "@id": `${buildUrl(locale)}/#website`
        },
        "breadcrumb": {
          "@id": `${url}#breadcrumb`
        },
        "mainEntity": {
          "@id": `${url}#product`
        },
        ...uniqueImages.length > 0 && {
          "primaryImageOfPage": {
            "@type": "ImageObject",
            "url": uniqueImages[0]
          }
        }
      },
      {
        "@type": "Product",
        "@id": `${url}#product`,
        "name": name || sku,
        "sku": sku,
        "productID": sku,
        "url": url,
        ...description && { "description": description },
        ...material && { "material": material },
        ...uniqueImages.length > 0 && { "image": uniqueImages },
        ...categoryPath.length > 0 && { "category": categoryPath.join(' > ') },
        ...additionalProperty.length > 0 && { "additionalProperty": additionalProperty },
        "brand": {
          "@id": `${buildUrl(locale)}/#organization`
        },
        "manufacturer": {
          "@id": `${buildUrl(locale)}/#organization`
        }
      },
      {
        ...buildBreadcrumb(locale, breadcrumbItems, buildCanonicalUrl),
        "@id": `${url}#breadcrumb`
      },
      {
        ...buildOrganization(locale, 'brief'),
        "@id": `${buildUrl(locale)}/#organization`
      }
    ]
  }

  return JSON.stringify(schema)
}
